import { z } from 'zod';

export const QuoteLineItemSchema = z.object({
  id: z.string().uuid(),
  quote_id: z.string().uuid(),
  category_id: z.string().uuid(),
  service_name: z.string().min(1, 'Service name is required'),
  description: z.string().optional(),
  current_cost: z.number().optional(),
  proposed_cost: z.number().optional(),
  is_included: z.boolean(),
  is_optional: z.boolean(),
  notes: z.string().optional(),
});

export const QuotePlanOptionSchema = z.object({
  id: z.string().uuid(),
  quote_id: z.string().uuid(),
  plan_name: z.string().min(1, 'Plan name is required'),
  plan_type: z.string().optional(),
  monthly_cost: z.number().positive().optional(),
  annual_cost: z.number().positive().optional(),
  features: z.any(), // JSON field
  is_recommended: z.boolean(),
});

export const QuoteImplementationMilestoneSchema = z.object({
  id: z.string().uuid(),
  quote_id: z.string().uuid(),
  milestone_name: z.string().min(1, 'Milestone name is required'),
  estimated_duration_days: z.number().positive().optional(),
  dependencies: z.array(z.string()),
  is_critical_path: z.boolean(),
  sort_order: z.number(),
});

export const QuoteComparisonBaselineSchema = z.object({
  id: z.string().uuid(),
  application_id: z.string().uuid(),
  current_provider_name: z.string().optional(),
  total_annual_cost: z.number().positive().optional(),
  cost_breakdown: z.any(), // JSON field
  service_gaps: z.any(), // JSON field
  satisfaction_rating: z.number().min(1).max(5).optional(),
});

export const ComparisonMetricsSchema = z.object({
  quote_id: z.string().uuid(),
  provider_name: z.string(),
  total_cost: z.number(),
  savings_amount: z.number(),
  savings_percentage: z.number(),
  implementation_days: z.number(),
  service_score: z.number().min(0).max(100),
  recommendation_score: z.number().min(0).max(100),
});

export const EnhancedPEOQuoteSchema = z.object({
  id: z.string().uuid(),
  application_id: z.string().uuid(),
  provider_id: z.string().uuid(),
  provider_name: z.string().optional(),
  company_name: z.string().optional(),
  quote_summary: z.string(),
  quote_file_url: z.string().url(),
  admin_notes: z.string().optional(),
  status: z.enum(['pending', 'accepted', 'declined', 'interested']),
  monthly_cost: z.number().positive().optional(),
  annual_cost: z.number().positive().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.string().uuid(),
  valid_until: z.string().optional(),
  is_submitted: z.boolean().optional(),
  line_items: z.array(QuoteLineItemSchema),
  plan_options: z.array(QuotePlanOptionSchema),
  implementation_timeline: z.array(QuoteImplementationMilestoneSchema),
});

// Validation functions
export const validateQuoteData = (quote: unknown) => {
  try {
    return EnhancedPEOQuoteSchema.parse(quote);
  } catch (error) {
    console.error('Quote validation failed:', error);
    throw new Error('Invalid quote data format');
  }
};

export const validateComparisonMetrics = (metrics: unknown) => {
  try {
    return z.array(ComparisonMetricsSchema).parse(metrics);
  } catch (error) {
    console.error('Comparison metrics validation failed:', error);
    throw new Error('Invalid comparison metrics format');
  }
};

export const validateBaseline = (baseline: unknown) => {
  try {
    return QuoteComparisonBaselineSchema.parse(baseline);
  } catch (error) {
    console.error('Baseline validation failed:', error);
    throw new Error('Invalid baseline data format');
  }
};